// ReadAloudButton — compact pill that speaks any text via OpenAI TTS.
// Used by the Daily Devotional card and the three Today Trio cards
// (On This Day · Something Good · Someone You Should Know).
//
// Self-managed audio: each button owns its own <Audio> instance so
// multiple cards on the same page can coexist (only one will play
// audio at a time though — see `pauseSiblings`).
//
// Design choices:
//   - h-8 px-3 text-[10px] — deliberately small so the cards stay
//     content-first. User asked: "shrink the Play out loud buttons
//     so they're not so big".
//   - One-shot fetch: TTS is requested once per text; subsequent
//     taps pause/resume the cached MP3 blob.
//   - Sibling-aware: when a new button starts playing it dispatches
//     a "hw:read-aloud:start" event so any other ReadAloudButton
//     pauses itself. Prevents two devotionals talking over each other.
import React, { useEffect, useRef, useState } from "react";
import { Play, Pause, Spinner } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import api from "../lib/api";
import { playOnce, stopOnce, onPlaybackEvent } from "../lib/singleAudio";
import { getActiveHdVoice } from "../lib/activeVoice";
import { useAudio } from "../context/AudioContext";

const BUS_EVENT = "hw:read-aloud:start";

export default function ReadAloudButton({
  text,
  testid = "read-aloud-button",
  // If `voice` is left undefined the button uses the user's chosen
  // profile from Settings → Voice (read via getActiveHdVoice). Pass
  // an explicit value only when a specific page MUST override the
  // user's pick (no such case today).
  voice,
  variant = "light", // "light" (cream-on-dark) | "dark" (dark-on-light)
  label,
  // Friendly label shown in the docked SingletonMiniPlayer. Defaults
  // to the visible button label but pages can pass a richer one
  // (e.g. "Today's devotional", "On this day"). Keeps the mini-player
  // copy human even when the button itself just says "Read aloud".
  nowPlayingLabel,
  // When true, fire the TTS fetch on mount (after 600ms idle) so the
  // audio Blob is already cached by the time the user taps Play.
  // Used on the Home + About hero bios so the founder's bio plays
  // instantly instead of waiting ~15-25s for OpenAI to synthesize
  // on first tap. Backend caches the result, so even if multiple
  // visitors hit at once, only the first triggers the slow path.
  prefetch = false,
  className = "",
}) {
  const { t } = useTranslation();
  const effectiveLabel = label || t("today.readAloud");
  // Read the active i18n language from AudioContext (same source
  // <Tr> uses) so the audio always matches whatever language the
  // user is browsing in. We do the actual translation lookup in
  // the click + prefetch handlers below (NOT via the async
  // useCaptionTranslation hook) so the audio never plays the
  // English fallback during a translation-in-flight window.
  const { language } = useAudio();
  const [playing, setPlaying] = useState(false);
  const [busy, setBusy] = useState(false);
  const myIdRef = useRef(Math.random().toString(36).slice(2));
  // Used to keep the underlying audio Blob alive between taps so we
  // can re-play / resume without re-fetching from OpenAI. We cache the
  // Blob — NOT the object URL — because `stopOnce()` in singleAudio.js
  // calls URL.revokeObjectURL on the current src whenever playback
  // halts. Caching the URL meant the second tap tried to play a
  // revoked URL and threw "Couldn't load the audio". By holding the
  // Blob and minting a fresh object URL per play, replay always works.
  const cachedBlobRef = useRef(null);
  const btnRef = useRef(null);

  // "Now reading" highlight: while this button's audio plays, glow the
  // card it lives in (same terracotta treatment as Bible verse-active)
  // and scroll it into view so the listener can follow along.
  useEffect(() => {
    const card = btnRef.current?.closest("[data-read-card], article, .rounded-2xl, .rounded-3xl, section");
    if (!card) return;
    if (playing) {
      card.classList.add("entry-active");
      card.scrollIntoView({ behavior: "smooth", block: "center" });
    } else {
      card.classList.remove("entry-active");
    }
    return () => card.classList.remove("entry-active");
  }, [playing]);

  // Stop my UI whenever ANOTHER read-aloud source starts. This now
  // covers both other ReadAloudButton instances AND the main chapter
  // reader (AudioContext fires the same bus event below).
  useEffect(() => {
    const onStart = (e) => {
      if (e?.detail?.id === myIdRef.current) return;
      // Halt whatever the singleton is playing right now. The
      // chapter reader uses its own <audio> element, so without
      // this our singleton audio would keep playing in parallel
      // until it finished on its own.
      stopOnce();
      setPlaying(false);
    };
    window.addEventListener(BUS_EVENT, onStart);
    // When the user swaps voices in Settings, blow away the cached
    // blob so the next tap re-fetches with the new voice. Without
    // this the button keeps replaying whatever it cached first.
    const onVoiceChange = () => {
      // No URL to revoke now (we cache the Blob, not the URL).
      // Just drop the cached Blob so the next tap re-fetches with
      // the new voice.
      cachedBlobRef.current = null;
      setPlaying(false);
    };
    window.addEventListener("hw:voice:changed", onVoiceChange);
    // Also reset UI when the singleton itself pauses/ends.
    const off = onPlaybackEvent((evt) => {
      if (evt === "pause" || evt === "ended" || evt === "error") {
        setPlaying(false);
      }
    });
    return () => {
      window.removeEventListener(BUS_EVENT, onStart);
      window.removeEventListener("hw:voice:changed", onVoiceChange);
      off();
    };
  }, []);

  // Drop the cached audio whenever the source text OR the active
  // language changes. Without this, a user who first played the bio
  // in English and then switched to Spanish would still hear the
  // cached English audio. Keying on `language` (not the async
  // `spokenText`) means the cache busts the instant the user picks
  // a new language — no race with the translation hook.
  useEffect(() => {
    cachedBlobRef.current = null;
  }, [text, language]);

  // Prefetch: fire the TTS request silently after the page is idle so
  // the Blob is ready in memory before the user taps Play. We delay
  // 150ms to let the initial render + critical network requests
  // settle, and bail if the user is on a save-data connection or
  // text is empty. We also re-fetch when the active language changes
  // so non-English visitors get their language pre-warmed too.
  // Errors during prefetch are swallowed — the click handler will
  // retry and surface a real toast if it also fails.
  useEffect(() => {
    if (!prefetch || !text || !text.trim()) return;
    // Respect data-saver / slow connections.
    const conn = typeof navigator !== "undefined" && navigator.connection;
    if (conn && (conn.saveData || /(^|-)2g$/.test(conn.effectiveType || ""))) {
      return;
    }
    if (cachedBlobRef.current) return;
    let cancelled = false;
    const tid = setTimeout(async () => {
      try {
        // Resolve the translation in the active language first so
        // prefetch warms the RIGHT audio. Backend mongo-cache means
        // this is ~150ms after the first call per (lang, text).
        let toSpeak = text;
        if (language && language !== "en") {
          try {
            const tx = await api.post("/translate-text", {
              texts: [text], lang: language, source: "en",
            });
            const got = tx?.data?.translations?.[0];
            if (got && got.trim()) toSpeak = got;
          } catch (_) {}
        }
        const v = voice || getActiveHdVoice();
        const tts = await api.post(
          "/tts/speak",
          { text: toSpeak, voice: v, model: "tts-1", speed: 1.0 },
          { responseType: "blob" }
        );
        if (!cancelled) cachedBlobRef.current = tts.data;
      } catch (_) {
        // Silent — user can still tap to retry; click handler will
        // show a real toast if the second attempt also fails.
      }
    }, 8000); // idle-delay: yields TTS capacity to user-initiated playback;
    // navigating away within 8s cancels these prefetches entirely.
    return () => {
      cancelled = true;
      clearTimeout(tid);
    };
  }, [prefetch, text, voice, language]);

  const handle = async () => {
    if (!text || !text.trim()) return;
    // Toggle pause on second tap
    if (playing) {
      stopOnce();
      setPlaying(false);
      return;
    }
    if (busy) return;
    setBusy(true);
    try {
      let blob = cachedBlobRef.current;
      if (!blob) {
        // Resolve the chosen voice at click time so picking a new
        // voice in Settings → Voice takes effect immediately without
        // a page reload. Falls back to a sensible profile when none
        // is stored.
        const v = voice || getActiveHdVoice();
        // Always re-resolve the translation in the active language
        // RIGHT NOW. We can't trust the `spokenText` from
        // useCaptionTranslation alone — if the user switched
        // language and tapped Play before the async batch landed,
        // spokenText would still be English. Hitting
        // /api/translate-text directly (backend Mongo-cached) means
        // we always send the right language to TTS in ~150ms after
        // the first call.
        let toSpeak = text;
        if (language && language !== "en") {
          try {
            const tx = await api.post("/translate-text", {
              texts: [text], lang: language, source: "en",
            });
            const got = tx?.data?.translations?.[0];
            if (got && got.trim()) toSpeak = got;
          } catch (_) {
            // If translation fails we still speak the English source
            // — better some audio than no audio.
          }
        }
        const tts = await api.post(
          "/tts/speak",
          { text: toSpeak, voice: v, model: "tts-1", speed: 1.0 },
          { responseType: "blob" }
        );
        blob = tts.data;
        cachedBlobRef.current = blob;
      }
      // Mint a fresh object URL every play. The singleton audio
      // module revokes the URL when playback stops, so a single
      // long-lived URL would die after the first end/pause.
      const url = URL.createObjectURL(blob);
      // Announce we're taking over the audio channel so siblings & the
      // chapter reader pause themselves. Also carries a friendly label
      // for the docked SingletonMiniPlayer.
      window.dispatchEvent(new CustomEvent(BUS_EVENT, { detail: { id: myIdRef.current, label: nowPlayingLabel || label } }));
      await playOnce(url);
      setPlaying(true);
    } catch (e) {
      toast.error(e?.response?.status === 503
        ? "Voice service isn't configured yet"
        : "Couldn't load the audio");
    } finally {
      setBusy(false);
    }
  };

  // Styling (Feb 2026): user asked us to drop the green/cream pill
  // backgrounds. The button now reads as plain forest-green text +
  // icon, with terracotta on hover. Both "light" and "dark" variants
  // converge to the same flat look — the variants only differ in
  // where they're mounted (cream cards vs darker hero surfaces) but
  // forest green stays readable on either.
  const colors = "text-forest hover:text-terracotta";

  return (
    <button
      type="button"
      ref={btnRef}
      onClick={handle}
      disabled={busy}
      data-testid={testid}
      aria-label={playing ? t("action.pause") : effectiveLabel}
      className={`inline-flex items-center gap-1.5 px-2 h-8 text-[10px] font-semibold tracking-[0.18em] uppercase disabled:opacity-60 transition-colors ${colors} ${className}`}
    >
      {busy ? (
        <Spinner size={10} weight="bold" className="animate-spin" />
      ) : playing ? (
        <Pause size={10} weight="bold" />
      ) : (
        <Play size={10} weight="fill" />
      )}
      <span>{busy ? t("action.loading") : playing ? t("action.pause") : effectiveLabel}</span>
    </button>
  );
}
